import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Package,
  Truck,
  PackageOpen,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  ExternalLink,
  Copy,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  RefreshCw,
  DollarSign,
  Instagram,
  Star,
  StickyNote
} from "lucide-react";
import { FaTiktok, FaWhatsapp } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { useAuthContext } from "@/lib/AuthProvider";
import { fetchOrderById, Order, cancelOrder } from "@/services/orderService";
import { fetchProductById } from "@/services/productService";
import { fetchCarriers, Carrier, generateCarrierTrackingUrl } from "@/services/carrierService";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/context/CartContext";
import { Product } from "@/data/products";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const { addToCart } = useCart();
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const isDelivered = order?.status === "delivered";
  const orderNote = order?.notes?.trim();

  const isBundleItem = (name?: string) => {
    if (!name) return false;
    return name.trim().toLowerCase().startsWith("bundle");
  };

  const handleNavigateToReviews = (productId?: string) => {
    if (!productId) return;
    navigate(`/product/${productId}#reviews`, {
      state: { fromOrderId: order?.id, focusReviewTab: true },
    });
  };

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange as EventListener);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange as EventListener);
    };
  }, []);

  // Load carriers on mount
  useEffect(() => {
    fetchCarriers().then(setCarriers).catch(() => {});
  }, []);

  // Get carrier name from code
  const getCarrierName = (carrierCode: string): string => {
    const carrier = carriers.find(c => c.code === carrierCode);
    return carrier?.name || carrierCode;
  };

  // Generate tracking URL from carrier template
  const getTrackingUrl = (carrierCode: string, trackingNumber: string): string | null => {
    const carrier = carriers.find(c => c.code === carrierCode);
    return carrier && trackingNumber ? generateCarrierTrackingUrl(carrier, trackingNumber) : null;
  };

  // Fetch order details
  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId || !user?.uid) {
        setError("Invalid order or user");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Pass userId for authorization check
        const orderData = await fetchOrderById(orderId, user.uid);
        
        if (!orderData) {
          setError("Order not found");
          return;
        }
        
        setOrder(orderData);
      } catch (err) {
        console.error("Error fetching order:", err);
        setError("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderId, user?.uid]);


  // Format date
  const formatDate = (dateString: string | Date | any) => {
    try {
      let date: Date;
      
      if (typeof dateString === 'string') {
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else if (dateString && typeof dateString === 'object') {
        if (typeof (dateString as any).toDate === 'function') {
          date = (dateString as any).toDate();
        } else if (typeof (dateString as any)._seconds === 'number') {
          const seconds = (dateString as any)._seconds;
          const nanoseconds = (dateString as any)._nanoseconds || 0;
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          throw new Error('Unrecognized date object format');
        }
      } else {
        throw new Error('Invalid date format');
      }
      
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date value');
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Unknown date';
    }
  };

  // Format delivery date (date only, no time)
  const formatDeliveryDate = (dateString: string | Date | any) => {
    try {
      let date: Date;
      
      if (typeof dateString === 'string') {
        date = new Date(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else if (dateString && typeof dateString === 'object') {
        if (typeof (dateString as any).toDate === 'function') {
          date = (dateString as any).toDate();
        } else if (typeof (dateString as any)._seconds === 'number') {
          const seconds = (dateString as any)._seconds;
          const nanoseconds = (dateString as any)._nanoseconds || 0;
          date = new Date(seconds * 1000 + nanoseconds / 1000000);
        } else {
          throw new Error('Unrecognized date object format');
        }
      } else {
        throw new Error('Invalid date format');
      }
      
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date value');
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Unknown date';
    }
  };

  // Helper function to get effective price (sale price if on sale, otherwise regular price)
  const getEffectivePrice = (item: any) => {
    if (item.onSale && item.onSalePrice && item.onSalePrice < item.price) {
      return item.onSalePrice;
    }
    return item.price;
  };

  // Helper function to check if an item is on sale
  const isItemOnSale = (item: any) => {
    return item.onSale && item.onSalePrice && item.onSalePrice < item.price;
  };

  // Currency conversion helpers using order's stored metadata
  const getOrderCurrency = () => {
    return order?.purchaseCurrency || 'USD';
  };

  const getOrderExchangeRates = () => {
    return order?.exchangeRateSnapshot || { USD: 1, CAD: 1.35, GBP: 0.8 };
  };

  // Convert USD price to order's display currency using stored exchange rate
  const getConvertedItemPriceFromOrder = (usdPrice: number) => {
    const currency = getOrderCurrency();
    const rates = getOrderExchangeRates();
    
    if (currency === 'USD') {
      return usdPrice;
    }
    
    const rate = rates[currency] || 1;
    return Math.ceil(usdPrice * rate);
  };

  // Format currency with order's currency symbol and code
  const formatOrderCurrency = (amount: number) => {
    const currency = getOrderCurrency();
    
    if (currency === 'USD') {
      return `$${amount.toFixed(2)} USD`;
    } else if (currency === 'CAD') {
      return `$${amount.toFixed(2)} CAD`;
    } else if (currency === 'GBP') {
      return `£${amount.toFixed(2)} GBP`;
    }
    
    return `$${amount.toFixed(2)}`;
  };

  // Calculate order total using per-item conversion for accuracy
  const getOrderTotal = () => {
    if (!order?.items) return 0;
    
    // Calculate converted subtotal (per-item conversion)
    const convertedSubtotal = order.items.reduce((total, item) => {
      const unitPriceUsd = getEffectivePrice(item);
      const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd);
      return total + (convertedUnitPrice * item.quantity);
    }, 0);
    
    // Calculate converted shipping (individual conversion)
    const baseShippingCost = order?.shipping?.cost || 0;
    const convertedShipping = order?.discount?.type === 'free_shipping' 
      ? 0 
      : getConvertedItemPriceFromOrder(baseShippingCost);
    
    // Calculate converted priority fee (individual conversion)
    const convertedPriorityFee = getConvertedItemPriceFromOrder(order?.shipping?.priorityFee || 0);
    
    // Calculate converted discount (individual conversion)
    const convertedDiscount = order?.discount?.type !== 'free_shipping' 
      ? getConvertedItemPriceFromOrder(order?.discount?.amount || 0) 
      : 0;
    
    // Total calculation: subtotal + shipping + priority - discount
    return convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount;
  };

  // Calculate order subtotal using per-item conversion
  const getOrderSubtotal = () => {
    if (!order?.items) return 0;
    
    return order.items.reduce((total, item) => {
      const unitPriceUsd = getEffectivePrice(item);
      const convertedUnitPrice = getConvertedItemPriceFromOrder(unitPriceUsd);
      return total + (convertedUnitPrice * item.quantity);
    }, 0);
  };

  // Get display status
  const getDisplayStatus = (status: string) => {
    switch (status) {
      case 'paid':
        return 'processing';
      case 'pending':
        return 'processing';
      case 'processed':
        return 'processed';
      default:
        return status;
    }
  };

  // Copy tracking number
  const copyTrackingNumber = (trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber);
    toast({
      title: "Copied!",
      description: "Tracking number copied to clipboard",
    });
  };

  // Handle Buy Again functionality
  const handleBuyAgain = async () => {
    if (!order) return;

    const processBuyAgain = async () => {
      try {
        // Fetch current product data for each item in the order
        const productPromises = order.items.map(async (orderItem) => {
          try {
            const currentProduct = await fetchProductById(orderItem.id);
            return {
              product: currentProduct,
              quantity: orderItem.quantity,
              originalName: orderItem.name
            };
          } catch (error) {
            console.warn(`Failed to fetch current data for product ${orderItem.id}:`, error);
            return {
              product: null,
              quantity: orderItem.quantity,
              originalName: orderItem.name
            };
          }
        });

        const productResults = await Promise.all(productPromises);
        
        let addedCount = 0;
        let skippedCount = 0;
        const skippedProducts: string[] = [];

        // Add available products to cart with current pricing
        for (const result of productResults) {
          if (result.product) {
            addToCart(result.product, result.quantity);
            addedCount++;
          } else {
            skippedCount++;
            skippedProducts.push(result.originalName);
          }
        }

        // Show appropriate success/warning message
        if (addedCount > 0 && skippedCount === 0) {
          toast({
            title: "Items Added to Cart",
            description: `${addedCount} item(s) from your previous order have been added to your cart with current pricing.`,
          });
        } else if (addedCount > 0 && skippedCount > 0) {
          toast({
            title: "Some Items Added to Cart",
            description: `${addedCount} item(s) added to cart. ${skippedCount} item(s) are no longer available: ${skippedProducts.join(', ')}.`,
            variant: "default",
          });
        } else {
          toast({
            title: "No Items Added",
            description: "Sorry, none of the items from this order are currently available.",
            variant: "destructive",
          });
          return;
        }

        // Navigate to cart page
        navigate('/cart');
      } catch (error) {
        console.error("Error adding items to cart:", error);
        toast({
          title: "Error",
          description: "Failed to add items to cart. Please try again.",
          variant: "destructive",
        });
      }
    };

    processBuyAgain();
  };

  // Handle Cancel Order functionality
  const handleCancelOrder = async () => {
    if (!order) return;

    try {
      setIsCancelling(true);
      // Use the backend API to cancel the order
      await cancelOrder(order.id);

      // Show success message
      toast({
        title: "Order Cancelled",
        description: `Order ${order.id} has been successfully cancelled.`,
      });

      // Close dialog
      setCancelDialogOpen(false);

      // Navigate back to orders page
      navigate('/orders');
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to cancel order. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // Helper function to get refund message for cancelled orders
  const getRefundMessage = () => {
    if (!order || order.status !== 'cancelled') return null;
    
    const paymentStatus = order.payment?.status;
    const refundProcessedAt = order.refund?.processedAt;
    
    // If payment status is 'succeeded' or 'refund_pending', show refund processing message
    if (paymentStatus === 'succeeded' || paymentStatus === 'refund_pending') {
      return {
        type: 'processing',
        message: 'Your refund is being processed and our team may reach out to you if necessary.'
      };
    }
    
    // If payment status is 'refunded', show refund completion message with processed date
    if (paymentStatus === 'refunded') {
      let message = 'Your refund has been processed and should appear on your statement within 5-10 business days from the refund date.';
      
      // If we have the refund processed date, include it in the message
      if (refundProcessedAt) {
        const processedDate = formatDate(refundProcessedAt);
        message = `Your refund was processed on ${processedDate} and should appear on your statement within 5-10 business days.`;
      }

        return {
          type: 'completed',
        message: message,
        processedDate: refundProcessedAt ? formatDate(refundProcessedAt) : null,
        };
    }
    
    return null;
  };

  // If loading, show loading spinner
  if (loading) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-500">Loading order details...</p>
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <div className="w-full max-w-lg mx-auto">
          {/* Error Icon Container */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                <XCircle className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>

          {/* Error Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Order Load Error
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              We encountered an issue loading this order. This might be a temporary problem with our servers.
            </p>
            
            {/* Error Details */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border-l-4 border-red-400">
              <p className="text-sm text-gray-700 font-mono break-words">
                {error}
              </p>
            </div>

            {/* Action Buttons */}
            {/* <div className="space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
                onClick={() => navigate('/orders')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Orders
              </Button>
            </div> */}
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              If the problem persists, please{" "}
              <a 
                href="mailto:support@jampackd.store?subject=Order%20Details%20Error%20Report&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20encountered%20an%20error%20while%20trying%20to%20view%20my%20order%20details%20on%20your%20website:%0D%0A%0D%0AError%20Details:%0D%0A%0D%0APlease%20help%20me%20resolve%20this%20issue.%0D%0A%0D%0AThank%20you!" 
                className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
              >
                contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If no order found, show not found message
  if (!order) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
        <div className="w-full max-w-lg mx-auto">
          {/* Not Found Icon Container */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <Package className="w-10 h-10 text-orange-600" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>

          {/* Not Found Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Order Not Found
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              We couldn't find the order you're looking for. It may have been removed or you might not have permission to view it.
            </p>
            
            {/* Action Buttons */}
            {/* <div className="space-y-3">
              <Button
                className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 transition-all duration-300 transform hover:scale-105 shadow-lg"
                onClick={() => navigate("/orders")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Orders
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-300"
                onClick={() => navigate('/products')}
              >
                Continue Shopping
              </Button>
            </div> */}
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Need help finding your order?{" "}
              <a 
                href="mailto:support@jampackd.store?subject=Order%20Not%20Found%20Issue&body=Hello%20JamPack'd%20Support%20Team,%0D%0A%0D%0AI%20am%20unable%20to%20find%20my%20order%20on%20your%20website:%0D%0A%0D%0APlease%20help%20me%20locate%20my%20order.%0D%0A%0D%0AThank%20you!" 
                className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200"
              >
                contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayStatus = getDisplayStatus(order.status);

  return (
    <div 
      className="bg-gray-50 py-16 px-4"
      style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        {/* <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/orders")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Orders
          </Button>
        </div> */}

        {/* Order Header Card */}
        <Card className="border-none shadow-lg overflow-hidden relative backdrop-blur-sm bg-white/80 border border-white/40 hover:shadow-2xl transition-all duration-500">
          <CardHeader className="bg-gradient-to-r from-jamaica-green/10 via-jamaica-yellow/10 to-jamaica-green/10 backdrop-blur-sm border-b border-jamaica-green/20 text-gray-900">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-full border border-jamaica-green/30 shadow-sm">
                  {displayStatus === "delivered" ? (
                    <Package className="h-8 w-8 text-jamaica-green" />
                  ) : displayStatus === "shipped" ? (
                    <Truck className="h-8 w-8 text-blue-600" />
                  ) : displayStatus === "processed" ? (
                    <Package className="h-8 w-8 text-purple-600" />
                  ) : displayStatus === "cancelled" ? (
                    <XCircle className="h-8 w-8 text-red-600" />
                  ) : (
                    <PackageOpen className="h-8 w-8 text-amber-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-2xl text-gray-900">Order</CardTitle>
                  <div className="text-sm font-mono text-gray-600 mt-1 break-all">
                    #{order.id}
                  </div>
                  <CardDescription className="text-gray-600">
                    Placed on {formatDate(order.createdAt)}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={cn(
                  "capitalize px-3 py-1 text-sm font-medium",
                  displayStatus === "delivered" 
                    ? "bg-green-500 text-white hover:bg-green-500" 
                    : displayStatus === "shipped"
                    ? "bg-blue-500 text-white hover:bg-blue-500"
                    : displayStatus === "processed"
                    ? "bg-purple-500 text-white hover:bg-purple-500"
                    : displayStatus === "cancelled"
                    ? "bg-red-500 text-white hover:bg-red-500"
                    : "bg-amber-500 text-white hover:bg-amber-500"
                )}>
                  {displayStatus === "delivered" && <CheckCircle className="h-4 w-4 mr-1" />}
                  {displayStatus === "shipped" && <Truck className="h-4 w-4 mr-1" />}
                  {displayStatus === "processed" && <Package className="h-4 w-4 mr-1" />}
                  {displayStatus === "cancelled" && <XCircle className="h-4 w-4 mr-1" />}
                  {displayStatus === "processing" && <Clock className="h-4 w-4 mr-1" />}
                  {displayStatus}
                </Badge>
                <span className="text-2xl font-bold">{formatOrderCurrency(getOrderTotal())}</span>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Order Note */}
            {orderNote && (
              <Card className="border-none shadow-md bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/15 border border-amber-200">
                      <StickyNote className="w-4 h-4 text-amber-600" />
                    </span>
                    Order Note
                  </CardTitle>
                  <CardDescription className="text-amber-800">
                    Added by the JamPack&apos;d team for your order.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-white/80 border border-amber-100 rounded-2xl p-4 sm:p-5 shadow-inner">
                    <p className="text-amber-900 whitespace-pre-line leading-relaxed text-sm sm:text-base">
                      {orderNote}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Order Items */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-jamaica-green" />
                  Order Items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={item.id || index} className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors duration-200">
                    {/* Primary Information Row */}
                    <div className="flex items-start gap-4 p-4">
                      <div className="flex-shrink-0">
                        <img 
                          src={item.image || '/placeholder-product.png'} 
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200 shadow-sm"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-product.png';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate text-base leading-tight">{item.name}</h4>
                            {isItemOnSale(item) && (
                              <div className="mt-1">
                                <span className="inline-flex items-center text-xs font-bold text-jamaica-yellow bg-jamaica-black px-2 py-1 rounded-md">
                                  🔥 ON SALE
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-lg text-gray-900">
                              {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item)) * item.quantity)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">Total</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Secondary Information Section */}
                    <div className="px-4 pb-4 space-y-3">
                      {/* Product Details Row */}
                      {((item as any).brand || ((item as any).netWeight && (item as any).weightUnit)) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {(item as any).brand && (
                            <span className="inline-flex items-center text-xs font-medium text-jamaica-green bg-jamaica-green/10 px-2.5 py-1 rounded-full border border-jamaica-green/20">
                              <span className="w-1.5 h-1.5 bg-jamaica-green rounded-full mr-1.5"></span>
                              {(item as any).brand}
                            </span>
                          )}
                          {(item as any).netWeight && (item as any).weightUnit && (
                            <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5"></span>
                              {(item as any).netWeight}{(item as any).weightUnit}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Quantity and Pricing Row */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                            <span className="text-sm font-medium text-gray-900">Qty: {item.quantity}</span>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {isItemOnSale(item) ? (
                            <div className="space-y-1">
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-sm text-gray-500 line-through">
                                  {formatOrderCurrency(getConvertedItemPriceFromOrder(item.price))}
                                </span>
                                <span className="text-xs text-gray-400">each</span>
                              </div>
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-sm font-semibold text-jamaica-green">
                                  {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item)))}
                                </span>
                                <span className="text-xs text-jamaica-green font-medium">each</span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {formatOrderCurrency(getConvertedItemPriceFromOrder(getEffectivePrice(item)))}
                              </span>
                              <span className="text-xs text-gray-500">each</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {isDelivered && !isBundleItem(item.name) && item.id && (
                        <div className="pt-3 mt-2 border-t border-dashed border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <p className="text-xs text-gray-500">
                            Loved this item? Share your experience with the JamPack&apos;d crew.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full sm:w-auto border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleNavigateToReviews(item.id);
                            }}
                          >
                            <Star className="h-4 w-4 mr-1 text-jamaica-yellow" />
                            Leave a Review
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>

            {/* Shipping Information */}
            {order.customer && (
              <Card className="border-none shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-jamaica-green" />
                    Shipping Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Delivery Address</h4>
                      <div className="text-gray-600">
                        <p className="font-medium">{order.customer.name}</p>
                        {order.customer.address && (
                          <>
                            <p>{order.customer.address.line1}</p>
                            {order.customer.address.line2 && <p>{order.customer.address.line2}</p>}
                            <p>
                              {order.customer.address.city}, {order.customer.address.state} {order.customer.address.postal_code}
                            </p>
                            <p>{order.customer.address.country}</p>
                          </>
                        )}
                        {(order.customer as any).phone && (
                          <p className="flex items-center gap-1 mt-2">
                            <Phone className="h-4 w-4" />
                            {(order.customer as any).phone}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Shipping Method</h4>
                      <div className="text-gray-600">
                        <p className="capitalize">
                          {order.shipping?.option || 'Standard'} Shipping
                        </p>
                        {order.shipping?.priorityProcessing && (
                          <p className="text-sm text-jamaica-green font-medium">
                            ✓ Priority Processing
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tracking Information */}
            {order.tracking && displayStatus !== "cancelled" && (
              <Card className="border-none shadow-md overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-jamaica-green/5 to-jamaica-green/10 border-b border-jamaica-green/10">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-jamaica-green/10 rounded-lg">
                      <Truck className="h-5 w-5 text-jamaica-green" />
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-gray-900">Package Tracking</span>
                      <p className="text-sm text-gray-600 font-normal">Track your order in real-time</p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tracking Details */}
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-500">Tracking Number</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyTrackingNumber(order.tracking!.trackingNumber)}
                            className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {getTrackingUrl(order.tracking.carrier, order.tracking.trackingNumber) ? (
                          <button
                            onClick={() => window.open(getTrackingUrl(order.tracking!.carrier, order.tracking!.trackingNumber)!, "_blank")}
                            className="font-mono text-lg font-semibold text-jamaica-green hover:text-jamaica-green/80 break-all cursor-pointer underline decoration-2 underline-offset-2 hover:decoration-jamaica-green/60 transition-all duration-200 text-left"
                          >
                            {order.tracking.trackingNumber}
                          </button>
                        ) : (
                          <p className="font-mono text-lg font-semibold text-gray-900 break-all">
                            {order.tracking.trackingNumber}
                          </p>
                        )}
                      </div>
                      
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-sm font-medium text-gray-500 mb-2">Shipping Carrier</p>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Truck className="w-4 h-4 text-jamaica-green" />
                          </div>
                          <span className="font-semibold text-gray-900">
                            {getCarrierName(order.tracking.carrier)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Info */}
                    <div className="space-y-4">
                      {order.tracking.estimatedDelivery && (
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">Estimated Delivery</span>
                          </div>
                          <p className="font-semibold text-blue-900">
                            {typeof order.tracking.estimatedDelivery === 'string' 
                              ? order.tracking.estimatedDelivery 
                              : formatDeliveryDate(order.tracking.estimatedDelivery)
                            }
                          </p>
                        </div>
                      )}
                      
                      {order.tracking.status && (
                        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Current Status</span>
                          </div>
                          <p className="font-semibold text-green-900 capitalize">
                            {order.tracking.status.replace(/_/g, ' ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {order.tracking.url && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <Button 
                        className="w-full bg-gradient-to-r from-jamaica-green to-green-600 hover:from-jamaica-green/90 hover:to-green-600/90 text-white font-semibold py-3 shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => window.open(order.tracking!.url!, '_blank')}
                      >
                        <ExternalLink className="h-5 w-5 mr-2" />
                        Track Package Live
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Summary */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-jamaica-green" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatOrderCurrency(getOrderSubtotal())}</span>
                </div>
                
                {order.shipping && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Shipping ({order.shipping.option || 'standard'})
                    </span>
                    <span className="font-medium">
                      {formatOrderCurrency(
                        getConvertedItemPriceFromOrder(
                          order.discount?.type === 'free_shipping'
                            ? (order.shipping.cost || 0) + (order.discount.amount || 0)
                            : (order.shipping.cost || 0)
                        )
                      )}
                    </span>
                  </div>
                )}
                
                {order.shipping?.priorityProcessing && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Priority Processing</span>
                    <span className="font-medium">{formatOrderCurrency(getConvertedItemPriceFromOrder(order.shipping.priorityFee || 0))}</span>
                  </div>
                )}
                
                {order.discount && (
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-semibold text-green-700">
                        {order.discount.type === 'free_shipping' 
                          ? '🎉 Free Shipping' 
                          : `🎉 ${order.discount.code}`}
                      </span>
                    </div>
                    <span className="font-semibold text-green-700">-{formatOrderCurrency(getConvertedItemPriceFromOrder(order.discount.amount))}</span>
                  </div>
                )}
                
                <Separator />
                
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-jamaica-green">{formatOrderCurrency(getOrderTotal())}</span>
                </div>
              </CardContent>
            </Card>

            {/* Order Actions */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle>Need Help?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.location.href = 'mailto:support@jampackd.store'}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
                
                {displayStatus === "delivered" && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={handleBuyAgain}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Buy Again
                  </Button>
                )}
                
                {displayStatus === "processing" && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Order
                  </Button>
                )}
                
                {displayStatus === "cancelled" && (
                  <div className="space-y-3">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 font-medium">Order Cancelled</p>
                      <p className="text-xs text-red-600 mt-1">
                        This order has been cancelled. If you have questions, please contact support.
                      </p>
                    </div>
                    
                    {/* Refund Message */}
                    {(() => {
                      const refundMessage = getRefundMessage();
                      if (refundMessage) {
                        return (
                          <div className={`p-3 rounded-lg border ${
                            refundMessage.type === 'processing' 
                              ? 'bg-yellow-50 border-yellow-200' 
                              : 'bg-green-50 border-green-200'
                          }`}>
                            <div className="flex items-start gap-2">
                              {refundMessage.type === 'processing' ? (
                                <DollarSign className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                              )}
                              <div>
                              <div>
                                <p className={`text-sm font-medium ${
                                  refundMessage.type === 'processing' 
                                    ? 'text-yellow-800' 
                                    : 'text-green-800'
                                }`}>
                                  {refundMessage.type === 'processing' ? 'Refund Processing' : 'Refund Processed'}
                                </p>
                                  {refundMessage.type === 'completed' && refundMessage.processedDate && (
                                    <p className="text-xs mt-0.5 text-green-700 font-medium">
                                      Processed: {refundMessage.processedDate}
                                    </p>
                                  )}
                                </div>
                                <p className={`text-xs mt-1 ${
                                  refundMessage.type === 'processing' 
                                    ? 'text-yellow-600' 
                                    : 'text-green-600'
                                }`}>
                                  {refundMessage.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                
                <div className="pt-3 border-t space-y-2">
                  <p className="text-sm font-medium text-gray-500">Support Hours</p>
                  <p className="text-sm text-gray-600">Mon-Fri, 9am-5pm EST</p>
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <span>Socials:</span>
                    <div className="flex items-center gap-2 text-gray-500">
                      <a
                        href="https://instagram.com/jampackdcarepackages"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-jamaica-green transition-colors"
                      >
                        <Instagram className="h-4 w-4" />
                      </a>
                      <a
                        href="https://tiktok.com/@jampackdcarepackages"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-jamaica-green transition-colors"
                      >
                        <FaTiktok className="h-4 w-4" />
                      </a>
                      <a
                        href="https://wa.me/18763276630?text=Hi%20Paul%21%20I%27d%20appreciate%20your%20help%20with%20something"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-jamaica-green transition-colors"
                      >
                        <FaWhatsapp className="h-4 w-4" />
                      </a>
                    </div>
                  </p>
                  <p className="text-sm text-gray-600">Phone: (876) 327-6630</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {order && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="font-medium text-sm">Order</div>
              <div className="text-xs font-mono text-gray-600 mt-1 break-all">{order.id}</div>
              <div className="text-sm text-gray-600 mt-1">
                {order.items.length} item(s) • {formatOrderCurrency(getOrderTotal())}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={isCancelling}
            >
              Keep Order
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetails; 
